import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Calculator, Info, Loader2 } from 'lucide-react';

/**
 * CostEstimateApple - Apple-styled cost estimate component
 * 
 * Props:
 * - feature: 'translation' | 'cat' | 'mtpe' | 'mtpe_pro' | 'proofread' | 'translation_qa'
 * - sourceWords: number - word count from source document (-1 means calculating)
 * - targetWords: number - word count from target document (for TQA, MTPE) (-1 means calculating)
 * - className: string - additional CSS classes
 * - variant: 'default' | 'compact' - display size
 */
const CostEstimateApple = ({ 
  feature = 'translation',
  sourceWords = 0,
  targetWords = 0,
  className = '',
  variant = 'default',
  planInfo = null
}) => {
  const { isDarkMode } = useTheme();
  // Check if we're in calculating state (-1 means file selected but not yet uploaded)
  const isCalculating = sourceWords === -1 || targetWords === -1;
  
  // Calculate cost based on feature type
  const getCostInfo = () => {
    switch (feature) {
      case 'translation':
      case 'cat':
        return {
          title: 'Cost Estimate',
          description: 'Translation charges 1:1 source word count.',
          wordType: 'source',
          words: sourceWords,
          rate: 1,
          estimatedCost: sourceWords > 0 ? sourceWords : 0
        };
      case 'mtpe':
        return {
          title: 'Cost Estimate',
          description: 'MTPE charges 75% of target document word count.',
          wordType: 'target',
          words: targetWords,
          rate: 0.75,
          estimatedCost: targetWords > 0 ? Math.ceil(targetWords * 0.75) : 0
        };
      case 'mtpe_pro':
        return {
          title: 'Cost Estimate',
          description: 'MTPE Pro charges 50% of target document word count.',
          wordType: 'target',
          words: targetWords,
          rate: 0.5,
          estimatedCost: targetWords > 0 ? Math.ceil(targetWords * 0.5) : 0
        };
      case 'proofread':
        return {
          title: 'Cost Estimate',
          description: 'Proofreading charges 30% of document word count.',
          wordType: 'source',
          words: sourceWords,
          rate: 0.3,
          estimatedCost: sourceWords > 0 ? Math.ceil(sourceWords * 0.3) : 0
        };
      case 'translation_qa':
        return {
          title: 'Cost Estimate',
          description: 'Translation QA charges 50% of target document word count.',
          wordType: 'target',
          words: targetWords,
          rate: 0.5,
          estimatedCost: targetWords > 0 ? Math.ceil(targetWords * 0.5) : 0
        };
      case 'super_review':
        return {
          title: 'Cost Estimate',
          description: 'Super Review charges 50% of translated document word count.',
          wordType: 'target',
          words: targetWords,
          rate: 0.5,
          estimatedCost: targetWords > 0 ? Math.ceil(targetWords * 0.5) : 0
        };
      default:
        return {
          title: 'Cost Estimate',
          description: 'Word-based pricing.',
          wordType: 'source',
          words: sourceWords,
          rate: 1,
          estimatedCost: sourceWords > 0 ? sourceWords : 0
        };
    }
  };

  const costInfo = getCostInfo();

  // Don't show if no file selected (words === 0)
  if (costInfo.words === 0) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-[#f5f5f7] dark:bg-[#1d1d1f] rounded-xl p-3 ${className}`} data-testid="cost-estimate">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#0071e3]" />
            {isCalculating ? (
              <span className="text-sm font-medium text-[#0071e3] flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Will calculate after upload
              </span>
            ) : (
              <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">
                {costInfo.words.toLocaleString()} {costInfo.wordType} words
              </span>
            )}
          </div>
          {!isCalculating && (
            <div className="text-sm font-semibold text-[#0071e3]">
              ~{costInfo.estimatedCost.toLocaleString()} words will be used
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{background: isDarkMode ? "#2c2c2e" : "#f5f5f7"}} className={`rounded-2xl p-5 ${className}`} data-testid="cost-estimate">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#0071e3]/10 flex items-center justify-center">
          <Calculator className="w-4 h-4 text-[#0071e3]" />
        </div>
        <h4 className="text-base font-semibold text-[#1d1d1f] dark:text-white">
          {costInfo.title}
        </h4>
      </div>
      
      {/* Description */}
      <p className="text-sm text-[#86868b] mb-4 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#86868b]" />
        <span>{costInfo.description}</span>
      </p>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl p-4 text-center" style={{background: isDarkMode ? "#3a3a3c" : "#ffffff"}}>
          <p className="text-xs text-[#86868b] uppercase tracking-wide mb-1">
            Total {costInfo.wordType} words
          </p>
          {isCalculating ? (
            <div className="flex items-center justify-center gap-2 text-[#0071e3]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Pending</span>
            </div>
          ) : (
            <p className="text-2xl font-bold font-sf-pro" style={{color: isDarkMode ? "#f5f5f7" : "#1d1d1f"}} data-testid="total-words">
              {costInfo.words.toLocaleString()}
            </p>
          )}
        </div>
        
        <div className="rounded-xl p-4 text-center" style={{background: isDarkMode ? "#3a3a3c" : "#ffffff"}}>
          <p className="text-xs text-[#86868b] uppercase tracking-wide mb-1">
            Words to use
          </p>
          {isCalculating ? (
            <div className="flex items-center justify-center gap-2 text-[#0071e3]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Pending</span>
            </div>
          ) : (
            <p className="text-2xl font-bold font-sf-pro" style={{color: isDarkMode ? "#409cff" : "#0071e3"}} data-testid="estimated-cost">
              {costInfo.estimatedCost.toLocaleString()}
              <span className="text-sm font-normal ml-1">words</span>
            </p>
          )}
        </div>
      </div>
      
      {/* Rate indicator */}
      {!isCalculating && costInfo.rate < 1 && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#0071e3]/10 text-xs font-medium text-[#0071e3]">
            {(costInfo.rate * 100).toFixed(0)}% of {costInfo.wordType} words
          </span>
        </div>
      )}
    </div>
  );
};

export default CostEstimateApple;
